import { Injectable } from '@angular/core';
import { PreferenceApiService } from '../api/preference.service';
import { GetUserPreferencesRequest } from '../../models/dtos/Classes/GetUserPreferencesRequest';
import { FetchMode } from '../../models/dtos/Enums/FetchMode';
import { firstValueFrom } from 'rxjs';
import { GetUserPreferencesResponse } from '../../models/dtos';
import { CommonDataService } from '../common-data.service';
import { UserProfileService } from '../common/user-profile.service';
import { CONSTANTS } from '../../Constants/Constants';

@Injectable()
export class SolPreferencesService {
  private _userPreferences: GetUserPreferencesResponse = null;
  // Last loaded/saved state, used to detect unsaved edits.
  private _baseline: GetUserPreferencesResponse = null;
  private _baselineKey: string = CONSTANTS.EMPTY_STRING;

  constructor(
    private readonly preferencesApiService: PreferenceApiService,
    private readonly userProfileService: UserProfileService,
    private readonly commonDataService: CommonDataService
  ) { }

  async getUserPreferencesApiResponse(): Promise<void> {
    const request: GetUserPreferencesRequest = {
      userId: this.userProfileService.userInfo?.gid ?? CONSTANTS.EMPTY_STRING,
      getPreferencesObject: FetchMode.Server,
      getFirePreferencesObject: FetchMode.Server,
      getESPreferencesObject: FetchMode.Server,
      getProposalPreferencesObject: FetchMode.Server,
      getCollaboratorPreferencesObject: true,
      getCostReviewerPreferencesObject: true,
      getTaxDetailsPreferencesObject: FetchMode.Server
    };

    const userPreferences = await firstValueFrom(this.preferencesApiService.getPreference(request));

    this._userPreferences = userPreferences;
    this.commonDataService.setPreferenceData(this._userPreferences);
    this.captureBaseline();
  }

  /** Marks the current preferences as the saved state. Call after load and after a successful save. */
  captureBaseline(): void {
    this._baseline = structuredClone(this._userPreferences);
    this._baselineKey = SolPreferencesService.serialize(this._baseline);
  }

  hasChanges(): boolean {
    return this._baseline !== null
      && SolPreferencesService.serialize(this._userPreferences) !== this._baselineKey;
  }

  // Null and '' compare equal, and the derived reviewerType text is ignored, because tabs normalise both on load.
  private static serialize(preferences: GetUserPreferencesResponse): string {
    return JSON.stringify(preferences, (key, value) =>
      key === 'reviewerType' || value === null || value === '' ? undefined : value) ?? CONSTANTS.EMPTY_STRING;
  }

  getUserPreferences(): GetUserPreferencesResponse {
    return this._userPreferences;
  }

  setUserPreferences(preferences: GetUserPreferencesResponse): void {
    this._userPreferences = preferences;
    this.commonDataService.setPreferenceData(this._userPreferences);
  }

  silentSetPreferences(updater: (prefs: GetUserPreferencesResponse) => void): void {
    updater(this._userPreferences);
    this.commonDataService.setPreferenceData(this._userPreferences);
  }

}
